import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useParams, useLocation, useNavigate, useBlocker } from "react-router";
import {
    Button,
    Tabs,
    TabsHeader,
    TabsBody,
    Tab,
    TabPanel,
    Menu,
    MenuHandler,
    MenuList,
    MenuItem,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Typography,
} from "@material-tailwind/react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useBeforeunload } from "react-beforeunload";
import { useDispatch } from "react-redux";
import { setSurveyDirty as setSurveyDirtyAction } from "store/meta";
import { fetcherSimple, fetcherFileUpload } from "utils/api";
import { getApiErrorMessage, classifyError } from "utils/api-error";
import { API_SERVER } from "utils/config";
import { createSurveyCacheInvalidator } from "utils/invalidate-survey-caches";
import ContentWrapper from "components/ui/content-wrapper";
import MaterialIcon from "components/material-icon";
import ProcessReportDialog from "components/dialog/process-report-dialog";
import ConfirmReportAdminDialog from "components/dialog/confirm-report-admin-dialog";
import ModerationTab from "components/form/moderation-tab";
import ReportsTab from "components/form/reports/reports-tab";
import ThemingTab from "pages/survey/theming/theming-tab";
import { useSurveyData } from "./build/hooks/use-survey-data";
import { useThemingData } from "./theming/hooks/use-theming-data";
import { useSurveyState } from "./build/hooks/use-survey-state";
import { useSurveyLifecycle } from "./build/hooks/use-survey-lifecycle";
import { useSurveySubmit } from "./build/hooks/use-survey-submit";
import { useQuestionManager } from "./build/hooks/use-question-manager";
import { useDialogState } from "./build/hooks/use-dialog-state";
import { useThemingState } from "./theming/hooks/use-theming-state";
import SettingsPanel from "./build/components/settings-panel";
import QuestionsPanel from "./build/components/questions-panel";
import AnswerThemingDialog from "components/dialog/answer-theming-dialog";
import ThemeAnswersDialog from "components/dialog/theme-answers-dialog";
import RespondentResponseSentimentListDialog from "components/dialog/respondent-response-sentiment-list";
import ResponsesSentimentListDialog from "components/dialog/responses-sentiment-list";
import RestartSurveyDialog from "components/dialog/restart-survey-dialog";
import ConfirmDialog from "components/dialog/confirm-dialog";
import CreateEditThemeDialog from "./theming/dialog/create-edit-theme-dialog";
import LifecycleDrawer from "./build/components/lifecycle-drawer";
import { renderSurveyStatus } from "./build/components/survey-status-card";
import {
    launchValidationSchema,
    validationSchemaSurvey,
    validationSchemaTheme,
    validationSchemaAnswerThemes,
    validationSchemaThemeAnswers,
} from "./build/validation-schemas";
import { shapeInitialSurveyValues } from "./build/initial-survey-data";
import {
    isStartedOrFinished,
} from "./build/lifecycle-visibility";

export default function BuildSurvey() {

    const { surveyId } = useParams();

    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {
        tabValue,
        setTabValue,
        logoDeletable,
        setLogoDeletable,
        instantActive,
        setInstantActive,
        surveyDirty,
        setSurveyDirty,
    } = useSurveyState({
        initialTabValue: location.state?.defaultTab || "settings",
    });

    // Ref to avoid stale closure in async contexts (e.g. setTimeout in add-question).
    const surveyDirtyRef = useRef(surveyDirty);
    surveyDirtyRef.current = surveyDirty;

    const [themesPage, setThemesPage] = useState(1);
    const {
        showSettingsDialog,
        setShowSettingsDialog,
        showQuestionDeleteDialog,
        setShowQuestionDeleteDialog,
        showProcessDialog,
        setShowProcessDialog,
        showPublishDialog,
        setShowPublishDialog,
        showPauseDialog,
        setShowPauseDialog,
        showResumeDialog,
        setShowResumeDialog,
        showRestartDialog,
        setShowRestartDialog,
        showFinishDialog,
        setShowFinishDialog,
        showConfirmReportDialog,
        setShowConfirmReportDialog,
        showRespondentResponseSentimentListDialog,
        setShowRespondentResponseSentimentListDialog,
        showResponsesSentimentListDialog,
        setShowResponsesSentimentListDialog,
        showTheming,
        openDrawer,
        setOpenDrawer,
    } = useDialogState();
    // Memoized so useSurveySubmit and toast useEffect have stable identity.
    const invalidateSurveyCaches = useMemo(
        () => createSurveyCacheInvalidator(surveyId),
        [surveyId],
    );
    const [modQShown, setModQShown] = useState(undefined);
    // Bridge ref: useThemingState runs before useThemingData. The hook
    // reads mutators lazily via a ref, so the 1-render lag is safe.
    const themingMutatorsRef = useRef({});

    const {
        themeEdit,
        setThemeEdit,
        answerTheming,
        showThemeDialog,
        setShowThemeDialog,
        showThemeDeleteDialog,
        setShowThemeDeleteDialog,
        showThemeAnswersDialog,
        setShowThemeAnswersDialog,
        showAnswerThemingDialog,
        setShowAnswerThemingDialog,
        handleThemeEditSubmit,
        handleDeleteThemeSubmit,
        handleAnswerThemingSubmit,
        handleThemeAnswersSubmit,
    } = useThemingState({
        surveyId,
        themesDataMutate: themingMutatorsRef.current.themesDataMutate,
        answerThemesMutate: themingMutatorsRef.current.answerThemesMutate,
        themeAnswersMutate: themingMutatorsRef.current.themeAnswersMutate,
        invalidateSurveyCaches,
        setModQShown,
    });

    const [confirmingAnswer, setConfirmingAnswer] = useState(false);
    const [processingReport, setProcessingReport] = useState(false);
    const [responseSentimentListId, setResponseSentimentListId] = useState(0);

    const [moderationFilter, setModerationFilter] = useState("reports");
    const [themeAnswersThemesState, setThemeAnswersThemesState] =
        useState("preset");
    const [questionSentimentIndex, setQuestionSentimentIndex] = useState(0);

    const [respondentSentimentData, setRespondentSentimentData] = useState({});
    const [respondentSentimentPage, setRespondentSentimentPage] = useState(1);
    const [responsesSentimentData, setResponsesSentimentData] = useState({});
    const [responseSentimentIdList, setResponseSentimentIdList] = useState([]);

    const [timestamp, setTimestamp] = useState(dayjs());

    const inputLogoFileRef = useRef(null);

    const {
        survey,
        surveyError,
        surveyIsLoading,
        surveyMutate,
        isBlocked,
        surveyQuestions,
        surveyQuestionsValidating,
        surveyQuestionsMutate,
        surveyRecipients,
        surveyRecipientsError,
        recipientsMutate,
        surveyAbuse,
        surveyAbuseError,
        surveyAbuseMutate,
        surveyRespondentAnswers,
        surveyRespondentAnswersError,
        surveyRespondentAnswersMutate,
        respondentsResponsesSentiment,
    } = useSurveyData({ surveyId, respondentSentimentPage });
    const {
        questionEditIndex,
        setQuestionEditIndex,
        questionToDelete,
        setQuestionToDelete,
        questionsToDelete,
        setQuestionsToDelete,
        addQuestion,
        cloneQuestion,
        moveQuestionUp,
        moveQuestionDown,
    } = useQuestionManager({ survey, surveyQuestions, surveyQuestionsMutate });

    // P8 builder shell. Raw resolver values: validationSchemaSurvey does
    // not list survey_website / policy_url / use_comprehend /
    // is_allow_anonymous, so Yup's cast would strip them from the
    // submitted payload; raw keeps those fields on the wire. One
    // recorded exception: answer_limit. This form registers the input
    // with a setValueAs number cast (question-settings-dialog), so the
    // wire carries numbers, and a cleared input becomes an omitted key
    // (the backend's exists-guard then keeps the prior value).
    const methods = useForm({
        mode: "onChange",
        resolver: yupResolver(
            isStartedOrFinished(survey?.state)
                ? Yup.object({})
                : validationSchemaSurvey,
            {},
            { raw: true },
        ),
        defaultValues: shapeInitialSurveyValues(survey, surveyQuestions),
    });
    const { reset, handleSubmit, getValues, formState } = methods;
    // P1: destructure the proxy flags before any conditional use.
    const { isDirty, isValid } = formState;
    // A fresh values snapshot for the LifecycleDrawer callbacks,
    // current at render time.
    const values = methods.watch();

    // P5/P8: re-baseline the store when SWR delivers or refreshes the
    // survey. Identity-keyed deps are safe: SWR's dequal compare keeps the
    // data reference through unchanged refetches, so this fires only when
    // the data actually changed.
    useEffect(() => {
        reset(shapeInitialSurveyValues(survey, surveyQuestions), {
            keepErrors: true,
        });
    }, [survey, surveyQuestions, reset]);

    // P8: drive the dirty guards (useBeforeunload, useBlocker, save
    // affordances) from RHF's value-based isDirty, both into the local
    // flag and the Redux copy other screens read.
    useEffect(() => {
        setSurveyDirty(isDirty);
        dispatch(setSurveyDirtyAction(isDirty));
    }, [isDirty, setSurveyDirty, dispatch]);

    const notify = () =>
        toast.success("URL copied successfully!", {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "light",
        });

    const {
        themesData,
        themesDataMutate,
        answerThemes,
        answerThemesValidating,
        answerThemesMutate,
        themeAnswers,
        themeAnswersValidating,
        themeAnswersMutate,
    } = useThemingData({
        surveyId,
        survey,
        surveyError,
        isBlocked,
        themesPage,
        answerTheming,
        themeEdit,
    });
    // Update the mutators ref so useThemingState handlers (which read
    // lazily via an internal ref) see the latest SWR mutate functions.
    themingMutatorsRef.current = {
        themesDataMutate,
        answerThemesMutate,
        themeAnswersMutate,
    };

    useEffect(() => {
        if (!respondentsResponsesSentiment?.data) return;
        if (respondentsResponsesSentiment) {
            const respondentsSentiment = structuredClone(
                respondentsResponsesSentiment.data,
            );
            let respondentsSentimentProcessed = {
                data: [],
                pager: respondentsResponsesSentiment.pager,
            };
            respondentsSentimentProcessed.data = respondentsSentiment.map(
                (respondent) => {
                    let maxValue = 0;
                    const values = Object.values(
                        respondent.respondent_sentiment,
                    );
                    maxValue = Math.max(0, ...values);
                    respondent.respondent_sentiment.highest = maxValue;
                    return respondent;
                },
            );
            // TODO: Fix this because it got borked by pagination and doesn't get all responses with sentiment
            let responsesSentiment = [];
            respondentsSentiment.forEach((respondent) => {
                if (respondent?.responses_with_sentiment?.length > 0) {
                    responsesSentiment.push(
                        ...respondent.responses_with_sentiment,
                    );
                }
            });
            if (respondentsSentimentProcessed) {
                setRespondentSentimentData(respondentsSentimentProcessed);
            }
            if (responsesSentiment) {
                setResponsesSentimentData(responsesSentiment);
            }
        }
    }, [respondentsResponsesSentiment]);

    const imageUrl =
        API_SERVER + `/v2/survey/${surveyId}/logo`;

    useBeforeunload(
        surveyDirty === true ? (event) => event.preventDefault() : null,
    );

    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            surveyDirty &&
            currentLocation.pathname !== nextLocation.pathname &&
            nextLocation.pathname !== "/logout"
    );

    // Reset Redux dirty state on unmount.
    useEffect(() => {
        return () => {
            dispatch(setSurveyDirtyAction(false));
        };
    }, [dispatch]);

    // Suppresses the "Survey saved" toast when set by silentSave.
    const silentSaveRef = useRef(false);

    // Appends through the RHF field-array callback; the store holds the new
    // row immediately, so callers that need the updated values (silentSave)
    // re-read getValues() instead of a hand-built return object.
    const handleCloneQuestion = (index, question, insertCallback) =>
        cloneQuestion(question, index, insertCallback);

    const setQuestionChop = (index) => {
        setQuestionToDelete(index);
        setShowQuestionDeleteDialog(true);
    };

    // P9: static launch schema, runtime deps via Yup context. The context
    // carries surveyRecipients (a missing list reads as "no recipients"
    // and would wrongly block launch). Returns false, after toasting the
    // failures, when launch validation rejects.
    const validateLaunch = async () => {
        try {
            await launchValidationSchema.validate(getValues(), {
                context: {
                    surveyRecipients,
                    pausedValidation: survey.state === "paused",
                },
            });
            return true;
        } catch (err) {
            if (err?.errors) {
                err.errors.map((errorString) => toast.error(`${errorString}`));
            } else {
                toast.error(err?.message || "Validation failed");
            }
            return false;
        }
    };

    const checkLaunch = async (values) => {
        if (survey.state !== "draft" && survey.state !== "paused") {
            toast.warn(
                "Survey has already been started and can no longer be edited! Pause survey for more functions.",
            );
            return;
        } else if (!instantActive && !dayjs(values.start_date).isValid()) {
            toast.error("Please ensure the survey has a valid start date");
            return;
        }
        // Persist before validating so work isn't lost on validation failure.
        await handleSurveySubmit(values);
        if (!(await validateLaunch())) return;
        toast.success("Survey is ready for Launch!");
    };

    const launchSurveySubmit = async (values) => {
        if (
            survey.state !== "draft" &&
            survey.state !== "published" &&
            survey.state !== "paused"
        ) {
            toast.warn(
                "Survey has already been started and can no longer be edited! Pause survey for more functions.",
            );
            return;
        } else if (!instantActive && !dayjs(values.start_date).isValid()) {
            toast.error("Please ensure the survey has a valid start date");
            return;
        }
        if (!(await validateLaunch())) return;
        await handleSurveySubmit(values);
        setShowPublishDialog(true);
    };

    const {
        handlePublishDialogSubmit,
        handlePauseDialogSubmit,
        handleResumeDialogSubmit,
        handleRestartDialogSubmit,
        handleFinishDialogSubmit,
    } = useSurveyLifecycle({
        surveyId,
        instantActive,
        setShowPublishDialog,
        setShowPauseDialog,
        setShowResumeDialog,
        setShowRestartDialog,
        setShowFinishDialog,
    });

    const handleLogoUploadClick = async () => {
        const result = await handleSurveySubmit(getValues());
        if (result?.status === "error" || result?.status === "idle") {
            return;
        }
        inputLogoFileRef.current?.click();
    };

    const updateLogoToast = (toastId, { type, render }) => {
        toast.update(toastId, {
            render,
            type,
            isLoading: false,
            autoClose: 5000,
            pauseOnFocusLoss: false,
            pauseOnHover: false,
            closeOnClick: true,
            closeButton: true,
        });
    };

    const logoUploadHandler = async (event) => {
        const csvSubmitToast = toast.loading("Saving Image...");
        if (!event.target.files) {
            toast.error("No file uploaded!");
            return;
        }
        const selectedFile = event.target.files[0];
        if (!selectedFile.type.includes("image/")) {
            toast.error(
                "Only Image Files are supported. Please ensure that the file you are loading is not excessively large and has the correct extension and name.",
            );
            return;
        }
        let imageResponse = undefined;
        try {
            imageResponse = await fetcherFileUpload(
                `/v2/survey/${surveyId}/logo`,
                "POST",
                "logo",
                selectedFile,
            );
        } catch {
            const serverError =
                imageResponse ||
                "unknown issue in uploading image. Please try a different size or type.";
            updateLogoToast(csvSubmitToast, {
                type: "error",
                render: `Error uploading Image, ${serverError}.`,
            });
            return;
        }
        if (imageResponse) {
            updateLogoToast(csvSubmitToast, {
                type: "success",
                render: "Logo Image Uploaded",
            });
            setTimestamp(dayjs());
            setLogoDeletable(true);
        } else {
            const serverError =
                imageResponse ||
                "unknown issue in uploading image. Please try a different size or type.";
            updateLogoToast(csvSubmitToast, {
                type: "error",
                render: `Error uploading Image, ${serverError}`,
            });
        }
    };

    const handleDeleteLogo = async () => {
        // Save first; if the save never completed there is nothing
        // consistent to delete the logo against.
        const result = await handleSurveySubmit(getValues());
        if (result?.status === "error" || result?.status === "idle") {
            return;
        }
        try {
            const deleteResponse = await fetcherSimple(
                `/v2/survey/${surveyId}/logo`,
                "DELETE",
            );
            if (deleteResponse === null || deleteResponse?.deleted) {
                toast.success("Logo Deleted!");
                setLogoDeletable(false);
                setTimestamp(dayjs());
            } else if (deleteResponse && deleteResponse.error) {
                toast.error(`Error deleting logo: ${deleteResponse.message}`);
            } else {
                toast.error("Error deleting logo!");
            }
        } catch (err) {
            toast.error(
                getApiErrorMessage(classifyError(err), "deleting logo"),
            );
        }
    };

    const handleSettingsOpen = (i) => {
        setQuestionEditIndex(i);
        setShowSettingsDialog(true);
    };

    const {
        submit: submitSurvey,
        status: submitStatus,
        error: submitError,
        partialSuccess: submitPartialSuccess,
        retryFailedUpserts,
    } = useSurveySubmit({
        survey,
        surveyId,
        surveyMutate,
        surveyQuestionsMutate,
        invalidateSurveyCaches,
        instantActive,
    });

    const questionsToDeleteRef = useRef(questionsToDelete);
    questionsToDeleteRef.current = questionsToDelete;

    const handleSurveySubmit = useCallback(async (values) => {
        // Clone at the boundary: every caller (form submit, launch paths,
        // logo handlers, silentSave, preview) hands in live nested store
        // refs, and the submit pipeline awaits several requests while the
        // user can keep editing. The clone freezes one consistent
        // snapshot for the whole pipeline.
        return submitSurvey(structuredClone(values), {
            questionsToDelete: questionsToDeleteRef.current,
            onQuestionsDeleted: () => setQuestionsToDelete([]),
        });
    }, [submitSurvey]);

    // Reads the form store at call time (tab switches, add-question): the
    // RHF store is always current.
    const silentSave = useCallback(() => {
        if (!surveyDirtyRef.current) return;
        if (submitStatus === "submitting") return;
        silentSaveRef.current = true;
        handleSurveySubmit(getValues());
    }, [handleSurveySubmit, submitStatus, getValues]);

    useEffect(() => {
        if (submitStatus === "success") {
            if (!silentSaveRef.current) {
                toast.success("Survey saved");
            }
        } else if (submitStatus === "partial-success" && submitPartialSuccess) {
            const { upsertFailures, deleteFailures } = submitPartialSuccess;
            const hasUpsertFailures = upsertFailures && upsertFailures.length > 0;
            const hasDeleteFailures = deleteFailures && deleteFailures.length > 0;

            if (hasUpsertFailures) {
                const failed = upsertFailures.length;
                const total = submitPartialSuccess.upsertTotal || failed;
                const deleteNote = hasDeleteFailures
                    ? ` Also failed to delete ${deleteFailures.length} question(s).`
                    : "";
                toast.error(
                    `Saved survey, but ${failed} of ${total} question update(s) failed.${deleteNote} ` +
                        "Click to retry failed upserts.",
                    {
                        autoClose: 10000,
                        onClick: () => {
                            retryFailedUpserts(
                                submitPartialSuccess.upsertFailures,
                            );
                        },
                    },
                );
            } else if (hasDeleteFailures) {
                toast.error(
                    `Saved survey, but failed to delete ${deleteFailures.length} question(s).`,
                    { autoClose: 10000 },
                );
            }
        } else if (submitStatus === "error" && submitError) {
            const code = submitError.code;
            if (code === "VALIDATION_FAILED") {
                toast.info(submitError.message);
            } else if (code === "SURVEY_NOT_EDITABLE") {
                // Friendly copy is already a complete sentence from
                // persistSurvey; show without the "Failed to save survey:"
                // prefix to avoid redundancy.
                toast.error(submitError.message);
            } else {
                toast.error(`Failed to save survey: ${submitError.message}`);
            }
        }
        if (submitStatus !== "idle" && submitStatus !== "validating" && submitStatus !== "submitting") {
            silentSaveRef.current = false;
        }
    }, [submitStatus, submitError, submitPartialSuccess, retryFailedUpserts]);

    const previewSurvey = async (values) => {
        if (survey.state !== "draft") {
            toast.warn(
                "Survey has already been started and can no longer be edited! Please pause the survey for more options.",
            );
            return;
        }
        await handleSurveySubmit(values);
        window.open(`/respond/${survey.nanoid}`, "_blank");
    };

    // Loading/error/blocked/not-loaded guards delegated to
    // renderSurveyStatus (returns null when the survey is ready to render).
    const earlyStatus = renderSurveyStatus({
        surveyIsLoading,
        surveyError,
        isBlocked,
        survey,
        navigate,
    });
    if (earlyStatus) {
        return earlyStatus;
    }

    const launchSurveyBody = (
        <>
            Once this survey is launched, you can no longer edit the
            questions. You will be able to pause the survey once launched
            make changes to:
            <br /><br />
            1. Manage recipient invitations.
            <br />
            2. Survey end time and date.
            <br /><br />
            Would you like to continue?
        </>
    );

    // Silent-save the current store contents before leaving a tab; the
    // store read happens at click time (P6 family).
    const switchTab = (tab) => {
        silentSave();
        setTabValue(tab);
    };

    return (
        <>
            <FormProvider {...methods}>
                <form
                    onSubmit={handleSubmit((values) =>
                        handleSurveySubmit(values),
                    )}
                >
                    {blocker.state === "blocked" && (
                        <Dialog open={true} handler={() => blocker.reset()}>
                            <DialogHeader className="dialog__header">
                                Unsaved Changes
                            </DialogHeader>
                            <DialogBody className="dialog__body">
                                <Typography>
                                    You have unsaved changes that will be lost.
                                    Leave without saving?
                                </Typography>
                            </DialogBody>
                            <DialogFooter className="dialog__footer">
                                <Button
                                    className="btn btn--secondary"
                                    variant="filled"
                                    onClick={() => blocker.reset()}
                                >
                                    Stay on Page
                                </Button>
                                <Button
                                    className="btn btn--primary ml-2"
                                    variant="filled"
                                    onClick={() => {
                                        dispatch(setSurveyDirtyAction(false));
                                        blocker.proceed();
                                    }}
                                >
                                    Leave
                                </Button>
                            </DialogFooter>
                        </Dialog>
                    )}
                    <ContentWrapper>
                        <div className="flex flex-row">
                            {/* Main tabs */}
                            <div className="w-full px-[2%] 2xl:px-[4%] mb-0 py-0 mt-2">
                                <div className="flex pt-4 flex-wrap justify-center">
                                    <div className="w-full flex flex-col">
                                        <Tabs value={tabValue} key={tabValue}>
                                            {/* Desktop tabs - visible on md+ screens */}
                                            <div className="hidden md:block">
                                                <TabsHeader className="px-4 mb-1">
                                                    <Tab value="settings" className="tab" onClick={() => switchTab("settings")}>
                                                            Details
                                                    </Tab>
                                                    <Tab value="questions" className="tab" onClick={() => switchTab("questions")}>
                                                            Questions
                                                    </Tab>
                                                    {!!(
                                                        isStartedOrFinished(survey.state)
                                                    ) && (
                                                        <Tab value="moderation" className="tab" onClick={() => switchTab("moderation")}>
                                                                Moderation
                                                        </Tab>
                                                    )}
                                                    {!!(
                                                        showTheming === true ||
                                                            isStartedOrFinished(survey.state)
                                                    ) && (
                                                        <Tab value="new_theming" className="tab" onClick={() => switchTab("new_theming")}>
                                                                Themes
                                                        </Tab>
                                                    )}
                                                    {!!(
                                                        isStartedOrFinished(survey.state)
                                                    ) && (
                                                        <Tab value="reports" className="tab" onClick={() => switchTab("reports")}>
                                                                Reports
                                                        </Tab>
                                                    )}
                                                </TabsHeader>
                                            </div>
                                            {/* Mobile dropdown - visible on screens <768px */}
                                            <div className="md:hidden px-4 mb-1">
                                                <Menu>
                                                    <MenuHandler>
                                                        <Button className="w-full justify-start gap-4 btn btn--primary">
                                                            <MaterialIcon size="xl" name="menu" className="mr-2" />
                                                                Survey Sections
                                                        </Button>
                                                    </MenuHandler>
                                                    <MenuList>
                                                        <MenuItem onClick={() => switchTab("settings")}>
                                                                Details
                                                        </MenuItem>
                                                        <MenuItem onClick={() => switchTab("questions")}>
                                                                Questions
                                                        </MenuItem>
                                                        {!!(
                                                            isStartedOrFinished(survey.state)
                                                        ) && (
                                                            <MenuItem onClick={() => switchTab("moderation")}>
                                                                    Moderation
                                                            </MenuItem>
                                                        )}
                                                        {!!(
                                                            showTheming === true ||
                                                                isStartedOrFinished(survey.state)
                                                        ) && (
                                                            <MenuItem onClick={() => switchTab("new_theming")}>
                                                                    Themes
                                                            </MenuItem>
                                                        )}
                                                        {!!(
                                                            isStartedOrFinished(survey.state)
                                                        ) && (
                                                            <MenuItem onClick={() => switchTab("reports")}>
                                                                    Reports
                                                            </MenuItem>
                                                        )}
                                                    </MenuList>
                                                </Menu>
                                            </div>
                                            <TabsBody>
                                                <SettingsPanel
                                                    survey={survey}
                                                    surveyId={surveyId}
                                                    instantActive={instantActive}
                                                    setInstantActive={setInstantActive}
                                                    timestamp={timestamp}
                                                    imageUrl={imageUrl}
                                                    logoDeletable={logoDeletable}
                                                    setLogoDeletable={setLogoDeletable}
                                                    inputLogoFileRef={inputLogoFileRef}
                                                    handleLogoUploadClick={handleLogoUploadClick}
                                                    handleDeleteLogo={handleDeleteLogo}
                                                    logoUploadHandler={logoUploadHandler}
                                                    surveyRecipients={surveyRecipients}
                                                    recipientsMutate={recipientsMutate}
                                                    surveyRecipientsError={surveyRecipientsError}
                                                />
                                                <QuestionsPanel
                                                    survey={survey}
                                                    onAutoSave={silentSave}
                                                    handleSettingsOpen={handleSettingsOpen}
                                                    handleCloneQuestion={handleCloneQuestion}
                                                    handleAddQuestion={addQuestion}
                                                    setQuestionChop={setQuestionChop}
                                                    moveQuestionUp={moveQuestionUp}
                                                    moveQuestionDown={moveQuestionDown}
                                                    showQuestionDeleteDialog={showQuestionDeleteDialog}
                                                    setShowQuestionDeleteDialog={setShowQuestionDeleteDialog}
                                                    questionToDelete={questionToDelete}
                                                    setQuestionToDelete={setQuestionToDelete}
                                                    setQuestionsToDelete={setQuestionsToDelete}
                                                    showSettingsDialog={showSettingsDialog}
                                                    setShowSettingsDialog={setShowSettingsDialog}
                                                    questionEditIndex={questionEditIndex}
                                                />
                                                {!!(
                                                    survey.state ===
                                                            "finished" ||
                                                        survey.state ===
                                                            "active" ||
                                                        survey.state ===
                                                            "paused"
                                                ) && (
                                                    <>
                                                        <ModerationTab
                                                            tabValue="moderation"
                                                            setShowProcessDialog={
                                                                setShowProcessDialog
                                                            }
                                                            setProcessingReport={
                                                                setProcessingReport
                                                            }
                                                            const={
                                                                processingReport
                                                            }
                                                            surveyAbuse={
                                                                surveyAbuse
                                                            }
                                                            surveyAbuseMutate={
                                                                surveyAbuseMutate
                                                            }
                                                            surveyAbuseError={
                                                                surveyAbuseError
                                                            }
                                                            moderationFilter={
                                                                moderationFilter
                                                            }
                                                            setModerationFilter={
                                                                setModerationFilter
                                                            }
                                                            surveyRespondentAnswers={
                                                                surveyRespondentAnswers
                                                            }
                                                            surveyRespondentAnswersError={
                                                                surveyRespondentAnswersError
                                                            }
                                                            modQShown={
                                                                modQShown
                                                            }
                                                            setModQShown={
                                                                setModQShown
                                                            }
                                                            setConfirmingAnswer={
                                                                setConfirmingAnswer
                                                            }
                                                            setShowConfirmReportDialog={
                                                                setShowConfirmReportDialog
                                                            }
                                                        />
                                                        <ReportsTab
                                                            tabValue="reports"
                                                            survey={
                                                                survey
                                                            }
                                                            surveyQuestions={
                                                                surveyQuestions
                                                            }
                                                            surveyId={
                                                                surveyId
                                                            }
                                                            surveyAbuse={surveyAbuse}
                                                            respondentsResponsesSentiment={
                                                                respondentsResponsesSentiment
                                                            }
                                                            setShowRespondentResponseSentimentListDialog={
                                                                setShowRespondentResponseSentimentListDialog
                                                            }
                                                            setResponseSentimentListId={
                                                                setResponseSentimentListId
                                                            }
                                                            respondentSentimentData={
                                                                respondentSentimentData
                                                            }
                                                            questionSentimentIndex={
                                                                questionSentimentIndex
                                                            }
                                                            setQuestionSentimentIndex={
                                                                setQuestionSentimentIndex
                                                            }
                                                            setShowResponsesSentimentListDialog={
                                                                setShowResponsesSentimentListDialog
                                                            }
                                                            setResponseSentimentIdList={
                                                                setResponseSentimentIdList
                                                            }
                                                            respondentSentimentPage={
                                                                respondentSentimentPage
                                                            }
                                                            setRespondentSentimentPage={
                                                                setRespondentSentimentPage
                                                            }
                                                        />
                                                        <TabPanel value="new_theming">
                                                            <ThemingTab
                                                                survey={
                                                                    survey
                                                                }
                                                                surveyId={
                                                                    surveyId
                                                                }
                                                                surveyQuestions={
                                                                    surveyQuestions
                                                                }
                                                                buildThemesDataMutate={
                                                                    themesDataMutate
                                                                }
                                                            />
                                                        </TabPanel>
                                                    </>
                                                )}
                                            </TabsBody>
                                        </Tabs>
                                        {!openDrawer && (
                                            <div className="fixed top-[max(20vh,5rem)] left-0 z-50 flex flex-col gap-1">
                                                <div
                                                    className={`w-[2rem] h-[11rem] md:w-[2.4rem] md:h-[12.8rem]
                                                            flex flex-col items-center justify-center bg-black hover:shadow-xl transition-all
                                                            cursor-pointer rounded-r-2xl`}
                                                    onClick={() => setOpenDrawer(true)}
                                                >
                                                    <MaterialIcon name="menu" className="text-white rotate-90 text-lg md:text-2xl" />
                                                    <div className="mt-2 flex items-center justify-center">
                                                        <span className="origin-center whitespace-nowrap text-white text-sm md:text-base"
                                                            style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)' }}>
                                                                SURVEY CONTROLS
                                                        </span>
                                                    </div>
                                                </div>
                                                <button
                                                    type="submit"
                                                    disabled={!isDirty}
                                                    aria-label="Save"
                                                    title={isDirty ? "Click to save unsaved changes" : "There are no unsaved changes"}
                                                    className={`w-[2rem] h-[4.4rem] md:w-[2.4rem] md:h-[4.8rem]
                                                            flex flex-col items-center justify-center transition-all
                                                            cursor-pointer rounded-r-2xl
                                                            ${isDirty
                                                ? "bg-cta text-black"
                                                : "bg-black text-white"
                                            }`}
                                                >
                                                    <MaterialIcon name="save" size="lg" className={isDirty ? "text-black" : "text-white"} />
                                                    <span className="mt-1 origin-center whitespace-nowrap text-sm md:text-base"
                                                        style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)' }}>
                                                            SAVE
                                                    </span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </ContentWrapper>
                    <LifecycleDrawer
                        open={openDrawer}
                        onClose={() => setOpenDrawer(false)}
                        survey={survey}
                        isValid={isValid}
                        values={values}
                        onCheckLaunch={checkLaunch}
                        onPreview={previewSurvey}
                        onLaunch={launchSurveySubmit}
                        onCopyUrl={() => {
                            notify();
                            navigator.clipboard.writeText(
                                window.location.origin +
                                    `/respond/${survey.nanoid}`,
                            );
                        }}
                        onPause={() => setShowPauseDialog(true)}
                        onFinish={() => setShowFinishDialog(true)}
                        onResume={() => setShowResumeDialog(true)}
                        onRestart={() => setShowRestartDialog(true)}
                    />
                </form>
            </FormProvider>
            <ConfirmDialog
                open={showPublishDialog}
                onClose={() => setShowPublishDialog(false)}
                onConfirm={handlePublishDialogSubmit}
                title="Launch This Survey"
                body={launchSurveyBody}
                confirmLabel="Launch"
            />
            <ConfirmDialog
                open={showPauseDialog}
                onClose={() => setShowPauseDialog(false)}
                onConfirm={handlePauseDialogSubmit}
                title="Pause Survey"
                body="This will manually set the survey to &ldquo;Paused&rdquo; before its time expires. Do you wish to proceed?"
                confirmLabel="Pause"
            />
            <ConfirmDialog
                open={showResumeDialog}
                onClose={() => setShowResumeDialog(false)}
                onConfirm={handleResumeDialogSubmit}
                title="Resume Survey"
                body="This will resume the survey from a paused state before its time expires. Do you wish to proceed?"
                confirmLabel="Resume"
            />
            <RestartSurveyDialog
                showDialog={showRestartDialog}
                handleClose={() => setShowRestartDialog(false)}
                survey={survey}
                surveyId={surveyId}
                handleRestartDialogSubmit={handleRestartDialogSubmit}
            />
            <ConfirmDialog
                open={showFinishDialog}
                onClose={() => setShowFinishDialog(false)}
                onConfirm={handleFinishDialogSubmit}
                title="Finish Survey"
                body="This will manually set the survey to &ldquo;Finished&rdquo; before its time expires, making it impossible to have any more responses submitted to it. Do you wish to proceed?"
                confirmLabel="Finish"
            />
            <ConfirmDialog
                open={showThemeDeleteDialog}
                onClose={() => {
                    setThemeEdit(undefined);
                    setShowThemeDeleteDialog(false);
                }}
                onConfirm={() => {
                    handleDeleteThemeSubmit(themeEdit.id);
                }}
                title="Remove Theme"
                body="This will permanently remove the Theme. Are you sure?"
                confirmLabel="Delete"
                confirmClassName="btn btn--danger ml-2"
            />
            <CreateEditThemeDialog
                open={showThemeDialog}
                onClose={() => setShowThemeDialog(false)}
                onSubmit={handleThemeEditSubmit}
                themeEdit={themeEdit}
                validationSchema={validationSchemaTheme}
            />
            {!answerThemesValidating && (
                <AnswerThemingDialog
                    showAnswerThemingDialog={showAnswerThemingDialog}
                    setShowAnswerThemingDialog={setShowAnswerThemingDialog}
                    answerThemes={answerThemes}
                    handleAnswerThemingSubmit={handleAnswerThemingSubmit}
                    themesData={themesData}
                    themesPage={themesPage}
                    onThemesPageChange={setThemesPage}
                    validationSchemaAnswerThemes={validationSchemaAnswerThemes}
                />
            )}
            {!(
                !themeEdit &&
                answerThemesValidating &&
                surveyQuestionsValidating &&
                themeAnswersValidating
            ) && (
                <ThemeAnswersDialog
                    showThemeAnswersDialog={showThemeAnswersDialog}
                    setShowThemeAnswersDialog={setShowThemeAnswersDialog}
                    themeEdit={themeEdit}
                    handleThemeAnswersSubmit={handleThemeAnswersSubmit}
                    validationSchemaThemeAnswers={validationSchemaThemeAnswers}
                    surveyRespondentAnswers={surveyRespondentAnswers}
                    themeAnswersThemesState={themeAnswersThemesState}
                    setThemeAnswersThemesState={setThemeAnswersThemesState}
                    surveyQuestions={surveyQuestions}
                    modQShown={modQShown}
                    setModQShown={setModQShown}
                    themeAnswers={themeAnswers}
                />
            )}
            <ProcessReportDialog
                processingReport={processingReport}
                showProcessDialog={showProcessDialog}
                setShowProcessDialog={setShowProcessDialog}
                surveyAbuseMutate={surveyAbuseMutate}
                surveyRespondentAnswersMutate={surveyRespondentAnswersMutate}
            />
            <ConfirmReportAdminDialog
                showDialog={showConfirmReportDialog}
                setShowConfirmReportDialog={setShowConfirmReportDialog}
                confirmingAnswer={confirmingAnswer}
                question={surveyRespondentAnswers?.[modQShown]}
                surveyAbuseMutate={surveyAbuseMutate}
            />
            <RespondentResponseSentimentListDialog
                showRespondentResponseSentimentListDialog={
                    showRespondentResponseSentimentListDialog
                }
                setShowRespondentResponseSentimentListDialog={
                    setShowRespondentResponseSentimentListDialog
                }
                responseSentimentListId={responseSentimentListId}
                setResponseSentimentListId={setResponseSentimentListId}
                respondentsResponsesSentiment={respondentsResponsesSentiment}
                respondentSentimentData={respondentSentimentData}
            />
            <ResponsesSentimentListDialog
                showResponsesSentimentListDialog={
                    showResponsesSentimentListDialog
                }
                setShowResponsesSentimentListDialog={
                    setShowResponsesSentimentListDialog
                }
                responsesSentimentData={responsesSentimentData}
                responseSentimentIdList={responseSentimentIdList}
            />
        </>
    );
}

